/**
 * Cardio (§5.4).
 *
 * La regla que manda acá: **sin nivel de resistencia, dos sesiones no son comparables.**
 * Más distancia en el mismo tiempo con el nivel cambiado no es progreso, es otra
 * configuración. Cuando falta el nivel, estas funciones devuelven el dato marcado como no
 * comparable en vez de meterlo en una tendencia y sacar una conclusión falsa.
 */

import { aTimestamp } from '../dates.ts';
import type { Fecha, RegistroCardio } from '../types.ts';

export const AVISO_SIN_NIVEL =
  'Falta el nivel de resistencia. Sin ese dato esta sesión no se puede comparar con las ' +
  'otras: más distancia puede ser sólo un nivel más bajo.';

export interface CardioContexto {
  registro: RegistroCardio;
  fecha: Fecha;
}

export function velocidadMedia(r: RegistroCardio): number | null {
  if (!(r.duracion_seg > 0) || !(r.distancia_km >= 0)) return null;
  return r.distancia_km / (r.duracion_seg / 3600);
}

export function caloriasPorMinuto(r: RegistroCardio): number | null {
  if (!(r.duracion_seg > 0) || !(r.calorias >= 0)) return null;
  return r.calorias / (r.duracion_seg / 60);
}

export function esComparable(r: RegistroCardio): boolean {
  return r.nivel_resistencia !== null;
}

export interface MetricasCardio {
  registro: RegistroCardio;
  fecha: Fecha;
  velocidadKmh: number | null;
  caloriasPorMinuto: number | null;
  comparable: boolean;
  aviso: string | null;
}

export function metricas(c: CardioContexto): MetricasCardio {
  const comparable = esComparable(c.registro);
  return {
    registro: c.registro,
    fecha: c.fecha,
    velocidadKmh: velocidadMedia(c.registro),
    caloriasPorMinuto: caloriasPorMinuto(c.registro),
    comparable,
    aviso: comparable ? null : AVISO_SIN_NIVEL,
  };
}

// ---------------------------------------------------------------------------
// Tendencia a nivel constante (§5.4)
// ---------------------------------------------------------------------------

export interface TendenciaNivel {
  nivel: number;
  sesiones: MetricasCardio[];
  /** Variación de distancia entre la primera y la última sesión de ese nivel, en km. */
  deltaDistanciaKm: number | null;
  /** Variación de velocidad media, en km/h. */
  deltaVelocidadKmh: number | null;
  /** Progreso cardiovascular real: más distancia al mismo nivel. */
  mejora: boolean | null;
}

/**
 * Agrupa por nivel de resistencia y calcula la tendencia dentro de cada grupo. Los
 * registros sin nivel se devuelven aparte: no se mezclan ni se les asigna un nivel por
 * defecto.
 */
export function tendenciaPorNivel(registros: CardioContexto[]): {
  porNivel: TendenciaNivel[];
  sinNivel: MetricasCardio[];
} {
  const grupos = new Map<number, CardioContexto[]>();
  const sinNivel: MetricasCardio[] = [];

  for (const c of registros) {
    const nivel = c.registro.nivel_resistencia;
    if (nivel === null) {
      sinNivel.push(metricas(c));
      continue;
    }
    const arr = grupos.get(nivel);
    if (arr) arr.push(c);
    else grupos.set(nivel, [c]);
  }

  const porNivel: TendenciaNivel[] = [...grupos.entries()]
    .map(([nivel, items]) => {
      const sesiones = items
        .sort((a, b) => aTimestamp(a.fecha) - aTimestamp(b.fecha))
        .map(metricas);
      const primera = sesiones[0];
      const ultima = sesiones[sesiones.length - 1];
      const hayDos = sesiones.length >= 2 && primera && ultima;

      const deltaDistanciaKm = hayDos
        ? ultima.registro.distancia_km - primera.registro.distancia_km
        : null;
      const deltaVelocidadKmh =
        hayDos && ultima.velocidadKmh !== null && primera.velocidadKmh !== null
          ? ultima.velocidadKmh - primera.velocidadKmh
          : null;

      return {
        nivel,
        sesiones,
        deltaDistanciaKm,
        deltaVelocidadKmh,
        mejora: deltaDistanciaKm === null ? null : deltaDistanciaKm > 0,
      };
    })
    .sort((a, b) => a.nivel - b.nivel);

  return { porNivel, sinNivel };
}

/**
 * Comparación puntual entre dos sesiones. Devuelve `comparable: false` cuando falta el
 * nivel en alguna o cuando los niveles difieren, con el motivo explícito: es la diferencia
 * entre informar y sacar una conclusión inventada.
 */
export function comparar(
  a: CardioContexto,
  b: CardioContexto,
): { comparable: boolean; motivo: string | null; deltaDistanciaKm: number | null } {
  const na = a.registro.nivel_resistencia;
  const nb = b.registro.nivel_resistencia;

  if (na === null || nb === null) {
    return { comparable: false, motivo: AVISO_SIN_NIVEL, deltaDistanciaKm: null };
  }
  if (na !== nb) {
    return {
      comparable: false,
      motivo: `Niveles distintos (${na} vs. ${nb}): la diferencia de distancia es de configuración, no de estado físico.`,
      deltaDistanciaKm: null,
    };
  }
  return {
    comparable: true,
    motivo: null,
    deltaDistanciaKm: b.registro.distancia_km - a.registro.distancia_km,
  };
}
